/**
 * The read-side IFD walk (NEW in DI-2-1; the spike's `readExifTags` in
 * `readers/index.ts` read 14 tags and pre-divided the rationals — exactly the
 * coercion DI-D13 c1 rules out).
 *
 * Walks IFD0, the ExifIFD (0x8769) and the GPS IFD (0x8825). The Interop
 * pointer (0xa005) is not followed and IFD1 (the thumbnail) is not read. The
 * three pointer tags themselves are offsets into the block, not data, so they
 * are not emitted as keys.
 *
 * Values are kept as the file has them:
 *   ASCII              → the string, trailing NULs stripped, nothing else
 *   BYTE/SHORT/LONG…   → a number (count 1) or a number[] (count > 1)
 *   RATIONAL/SRATIONAL → [n, d] (count 1) or [n, d][] — never divided
 *   UNDEFINED          → `{ bytes: n }`, except the known text tags
 *                        (`UserComment` & co. decoded with their charset
 *                        prefix; `ExifVersion` / `FlashpixVersion` as text)
 *   MakerNote          → `{ bytes: n }` and nothing else (DI-D20 c4)
 */

import type { ExifValue, Rational } from "../types.js";
import {
  CHARSET_TEXT_TAGS,
  GPS_TAGS,
  TIFF_EXIF_TAGS,
  VERSION_TEXT_TAGS,
  tagName,
} from "../exifTags.js";
import {
  TAG_EXIF_IFD,
  TAG_GPS_IFD,
  TAG_INTEROP_IFD,
  readTiffHeader,
} from "./tiff.js";

const TYPE_SIZE: Readonly<Record<number, number>> = {
  1: 1, // BYTE
  2: 1, // ASCII
  3: 2, // SHORT
  4: 4, // LONG
  5: 8, // RATIONAL
  6: 1, // SBYTE
  7: 1, // UNDEFINED
  8: 2, // SSHORT
  9: 4, // SLONG
  10: 8, // SRATIONAL
  11: 4, // FLOAT
  12: 8, // DOUBLE
};

const POINTERS = new Set([TAG_EXIF_IFD, TAG_GPS_IFD, TAG_INTEROP_IFD]);

/** Guard against a corrupt count asking for megabytes of numbers. */
const MAX_NUMERIC_VALUES = 4096;

const latin1 = new TextDecoder("latin1");
const utf8 = new TextDecoder("utf-8");

function stripTrailingNuls(s: string): string {
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === 0) end--;
  return s.slice(0, end);
}

function decodeCharsetText(bytes: Uint8Array, little: boolean): string {
  const prefix = latin1.decode(bytes.subarray(0, 8));
  const body = bytes.subarray(8);
  if (prefix.startsWith("UNICODE")) {
    const dec = new TextDecoder(little ? "utf-16le" : "utf-16be");
    return stripTrailingNuls(dec.decode(body));
  }
  // "ASCII\0\0\0", "JIS\0\0\0\0\0" (rare, treated as bytes) and the all-NUL
  // "undefined" prefix — UTF-8 is a superset of the ASCII the cameras write.
  return stripTrailingNuls(utf8.decode(body));
}

function readValue(
  view: DataView,
  bytes: Uint8Array,
  at: number,
  type: number,
  count: number,
  little: boolean,
  name: string,
): ExifValue | undefined {
  const size = (TYPE_SIZE[type] ?? 0) * count;
  if (size === 0 || at < 0 || at + size > bytes.byteLength) return undefined;
  switch (type) {
    case 2:
      return stripTrailingNuls(latin1.decode(bytes.subarray(at, at + size)));
    case 7: {
      if (CHARSET_TEXT_TAGS.has(name) && count >= 8)
        return decodeCharsetText(bytes.subarray(at, at + size), little);
      if (VERSION_TEXT_TAGS.has(name))
        return latin1.decode(bytes.subarray(at, at + size));
      return { bytes: count };
    }
    case 5:
    case 10: {
      if (count > MAX_NUMERIC_VALUES) return { bytes: size };
      const out: Rational[] = [];
      for (let i = 0; i < count; i++) {
        const o = at + i * 8;
        out.push(
          type === 5
            ? [view.getUint32(o, little), view.getUint32(o + 4, little)]
            : [view.getInt32(o, little), view.getInt32(o + 4, little)],
        );
      }
      const [only] = out;
      return count === 1 && only ? only : out;
    }
    default: {
      if (count > MAX_NUMERIC_VALUES) return { bytes: size };
      const read = (o: number): number => {
        switch (type) {
          case 1:
            return view.getUint8(o);
          case 3:
            return view.getUint16(o, little);
          case 4:
            return view.getUint32(o, little);
          case 6:
            return view.getInt8(o);
          case 8:
            return view.getInt16(o, little);
          case 9:
            return view.getInt32(o, little);
          case 11:
            return view.getFloat32(o, little);
          default:
            return view.getFloat64(o, little);
        }
      };
      const step = TYPE_SIZE[type] ?? 1;
      const out: number[] = [];
      for (let i = 0; i < count; i++) out.push(read(at + i * step));
      const [first] = out;
      return count === 1 && first !== undefined ? first : out;
    }
  }
}

export interface ExifRead {
  tags: Record<string, ExifValue>;
  byteOrder: "II" | "MM";
}

/**
 * Reads the TIFF block of an EXIF APP1 (the payload after `Exif\0\0`).
 * Returns null when the block is not a TIFF structure at all.
 */
export function readExif(tiff: Uint8Array): ExifRead | null {
  const header = readTiffHeader(tiff);
  if (!header) return null;
  const { view, little } = header;
  const tags: Record<string, ExifValue> = {};
  const seen = new Set<number>();

  const walkIfd = (
    offset: number,
    table: Readonly<Record<number, string>>,
  ): void => {
    if (seen.has(offset) || offset < 8 || offset + 2 > tiff.byteLength) return;
    seen.add(offset);
    const n = view.getUint16(offset, little);
    for (let i = 0; i < n; i++) {
      const e = offset + 2 + i * 12;
      if (e + 12 > tiff.byteLength) return;
      const tag = view.getUint16(e, little);
      const type = view.getUint16(e + 2, little);
      const count = view.getUint32(e + 4, little);
      if (table === TIFF_EXIF_TAGS && POINTERS.has(tag)) {
        const target = view.getUint32(e + 8, little);
        if (tag === TAG_EXIF_IFD) walkIfd(target, TIFF_EXIF_TAGS);
        else if (tag === TAG_GPS_IFD) walkIfd(target, GPS_TAGS);
        // Interop (0xa005) is deliberately not followed.
        continue;
      }
      const name = tagName(table, tag);
      if (name === "MakerNote") {
        tags[name] = { bytes: count };
        continue;
      }
      const size = (TYPE_SIZE[type] ?? 0) * count;
      const at = size > 4 ? view.getUint32(e + 8, little) : e + 8;
      const value = readValue(view, tiff, at, type, count, little, name);
      if (value !== undefined && !(name in tags)) tags[name] = value;
    }
  };

  walkIfd(header.ifd0, TIFF_EXIF_TAGS);
  return { tags, byteOrder: little ? "II" : "MM" };
}
